import type {UserContextProps} from '@containers/App/UserProvider'
import {useUserContext} from '@containers/App/UserProvider'
import type {ServiceCapabilities} from '@interfaces/user'

// When the backend payload has no service_capabilities (older organizations
// deployment) fall back to fully visible so we never hide an area before the
// capability API is live. A present payload is authoritative (owners/admins/
// superusers already come back as full read+write from the serializer). This
// mirrors the fallback in useCurrentUser.
const ALL_CAPABILITIES_VISIBLE: ServiceCapabilities = {
  ai: {read: true, write: true},
  edge: {read: true, write: true},
  anomaly_detection: {read: true, write: true},
}

/**
 * Per-service (AI / Edge / Anomaly Detection) read/write capabilities used to
 * gate product areas by visibility (`read`) and configuration controls
 * (`write`). Reads straight from the user context so consumers that only need
 * capabilities don't pull in the rest of `useCurrentUser`.
 *
 * Semantics: `read` = may see/use the area; `write` = may create/edit/delete/
 * configure. Owners/admins/superusers already come back full read+write from the
 * backend, so callers only need to check the booleans.
 */
export const useServiceCapabilities = (): ServiceCapabilities => {
  // Tolerate a missing UserProvider (e.g. the public Alpinist demo embed renders a minimal
  // provider stack) — same guard as ChatMessageList and the chart components.
  const {currentUser} = (useUserContext() as UserContextProps | null) ?? {currentUser: null}
  return currentUser?.userprofile?.service_capabilities ?? ALL_CAPABILITIES_VISIBLE
}
